import { Overlay, OverlayContainer } from '@angular/cdk/overlay';
import {
  Injectable, Injector, StaticProvider, Type,
} from '@angular/core';
import {from, Observable} from 'rxjs';
import { skip, tap } from 'rxjs/operators';
import {ApModalTokens} from './ApModalComponentType';
import {AnyModalComponent} from './AnyModalComponent';

import {ApActiveModal} from './ApActiveModal';
import { ApDialogConfig, ApDialogRef } from './ApDialogConfig';

import { Dialog, DialogConfig } from '@angular/cdk/dialog';
import {
  destroyManualInjector,
  destroyManualInjectorWhen,
  ExactlyPartial,
  NgZoneService,
  stringifyClass,
} from 'webcommon/shared';

import { BrowserService, ScopeProvider } from 'webcommon/legacy-common';
import { CgmChatService } from 'webcommon/common-api';

// This is a type useful for situations where you need to reference the generic type of a class via type inferencing
// when there are multiple generic types declared on a function or class.
// Ideally, the function below would have the type parameters like this:
// <TComponent extends ApBaseModalComponent<TResult>, TResult>
// but then TResult will not get inferred correctly, meaning you would have to tell the function
// what type it needs to be in the <> when calling it.
// see https://stackoverflow.com/q/58847047/5335355 for an example
type InferModalResult<T> = T extends AnyModalComponent<infer TResult> ? TResult : never;
export type InferModalData<T> = T extends AnyModalComponent<any, any> ? T['modalData'] : never;

@Injectable({
  providedIn: 'root',
})
// This is a wrapper around BsModalService to provide more specific
// for our use of the modals.
// Also, it provides typing around stuff that ngx-boostrap doesn't have types for!
export class ApModalService {
  private documentElement = this.browserService.getDocument().documentElement;
  constructor(
    private dialog: Dialog,
    private cgmChatService: CgmChatService,
    // For a root service like this, this is a module Injector, which is actually an instance of R3Injector at runtime.
    // Unless it's provided separately than the 'root' module, then that specific case have a different instance.
    // (https://github.com/angular/angular/blob/main/packages/core/src/di/r3_injector.ts)
    private moduleInjector: Injector,
    private ngZoneService: NgZoneService,

    private overlay: Overlay,
    private browserService: BrowserService,
    private overlayContainer: OverlayContainer,
  ) {
    this.dialog.afterAllClosed.pipe(
      // skip 1 because it should emit right after subscribed the first time
      skip(1),
      tap(() => {
        this.makeCompatibleWithBootstrapModalAllClosed();
      }),
    ).subscribe();
  }

  // If you see an error for
  // "Types of property 'someProperty' are incompatible. Type 'someType' is not assignable to type 'never'",
  // then it's because 'someProperty' doesn't exist on TComponent.
  // So it's probably a typo, or you haven't yet added that property to 'TComponent'.
  //
  // If you see an error for
  // "Types of property 'someProperty2' are incompatible. Type 'someType2' is not assignable to type 'someOtherType'",
  // then it's because 'someProperty2' probably does exist on 'TComponent', but you're giving it the wrong type.
  //
  // This is essentially making sure that the properties passed on the 'initialState' match 'TComponent',
  // which is most likely necessary for 'TComponent' to function properly.
  // The type that 'TComponentState' extends from has to be an intersection with some variant of a 'Partial<TComponent>'
  // so as to not require the instance methods to be filled in on the partial type.
  //
  // More notes:
  // If 'TComponentState' just only extended 'Partial<TComponent>',
  // then it would allow excess properties on the 'initialState' that don't match 'TComponent'
  // (This is intended behavior by TypeScript: https://github.com/microsoft/TypeScript/issues/31872).
  // I can't think of a reason for why it would ever be necessary to allow excess properties on the 'initialState'
  // for whatever modal component is being used here, but i guess this could be changed in the future.
  showComponentModal<TComponent extends AnyModalComponent<any>, TComponentState extends ExactlyPartial<TComponent, TComponentState>>(
    component: Type<TComponent>,
    config?: ApDialogConfig<InferModalData<TComponent>, TComponentState>,
  ): Promise<InferModalResult<TComponent>> {
    const initialChatBotState = this.cgmChatService.getHiddenState();
    this.cgmChatService.hideChatBot();
    return this.ngZoneService.runIfNecessary(() => {
      return this.showComponentModal_disregardZone(component, config);
    }).finally(() => {
      // https://github.com/angular/angular/pull/49589
      // https://github.com/angular/angular/pull/46462
      this.cgmChatService.setHiddenState(initialChatBotState);
    });
  }

  // This function has the main logic, without caring about whether it's in the right zone,
  // which is why it's wrapped in a call to NgZoneService.runIfNecessary() above.
  // eslint-disable-next-line max-len
  protected showComponentModal_disregardZone<TComponent extends AnyModalComponent<any>, TComponentState extends ExactlyPartial<TComponent, TComponentState>>(
    component: Type<TComponent>,
    config?: ApDialogConfig<InferModalData<TComponent>, TComponentState>,
  ): Promise<InferModalResult<TComponent>> {
    const resultPromise = new Promise<InferModalResult<TComponent>>((resolve, reject) => {
      if (config?.injector === this.moduleInjector) {
        // config.injector should normally be a NodeInjector from a component, to be able to inject non-singleton services in the modal.
        // Currently, it makes no sense to pass in a root module injector, as that is already the default.
        // eslint-disable-next-line max-len
        throw new Error(`Error trying to open modal '${stringifyClass(component)}'. DialogConfig.injector matches root module injector; this is probably a mistake, since that is the default`);
      }


      const activeModal = new ApActiveModal<InferModalResult<TComponent>>(resolve, reject);

      // Create a new simulated element Injector specifically for UpgradeComponents that may exist inside of this modal,
      // so that they can access the $scope properly.
      // (I'm calling it a simulated element injector, because it's not really an element injector,
      // like the one that would get injected into a component, but it does the necessary task of providing $scope)
      // And by creating a new Injector each time through here, means that the ScopeProvider will provide a new child scope
      // linked to the $rootScope for each new modal, which is what we want.
      // Then each UpgradeComponent inside of this modal will have this new scope as a parent, which is correct.
      // (as opposed to depending on the ScopeProvider being a provider for the whole module, which means a single scope gets created
      // the first time an UpgradeComponent needs one, and then is shared with all future ones after that in the same module,
      // which feels wrong)
      // The Injector that is injected into this class, and into the modal class is a module injector,
      // which doesn't provide $scope.
      const elementInjector = Injector.create({
        name: 'ApModalService-ElementInjector',
        parent: this.moduleInjector,
        providers: [
          ScopeProvider,
        ],
      });

      // ApActiveModal always needs to get filled in on the initialState for the modal component to return properly back to the caller
      const completeConfig = config || new ApDialogConfig<InferModalData<TComponent>, TComponentState>();
      completeConfig.elementInjector = elementInjector;
      const activeModalProv: StaticProvider = {
        provide: ApActiveModal,
        useValue: activeModal,
      };
      const _providers = completeConfig.providers || [];
      if (typeof _providers === 'function') {
        completeConfig.providers = (...args) => {
          return [
            activeModalProv,
            ..._providers(...args),
          ];
        };
      } else {
        completeConfig.providers = [
          activeModalProv,
          ..._providers,
        ];
      }
      this.makeCompatibleWithBootstrapModal(completeConfig);

      try {
        const dialogRef = this.dialog.open(component, completeConfig);
        destroyManualInjectorWhen(elementInjector, dialogRef.closed);
        this.makeCompatibleWithBootstrapModalAfterOpen(dialogRef);
      } catch (e) {
        destroyManualInjector(elementInjector);
        const messagePrefix = `Error trying to open modal '${stringifyClass(component)}'`;
        console.error(messagePrefix, e);
        activeModal.dismiss(e);
        throw e;
      }
    });

    return resultPromise;
  }

  private makeCompatibleWithBootstrapModalAllClosed(): void {
    // after all the dialogs are closed, remove this class from the document
    this.documentElement.classList.remove('ap-cdk-bootstrap-modal-override');
    this.overlayContainer.getContainerElement().classList.remove('ap-cdk-bootstrap-modal-override');
  }

  private makeCompatibleWithBootstrapModal(config: DialogConfig): void {
    // mimic the default behavior of ngx-bootstrap config for backwards compatibility,
    // so that all the current modals don't have to be refactored to work/look correctly.
    // TODO: We should probably take this out eventually to go more off of material standards
    // by using this: https://v15.material.angular.io/components/dialog/overview
    config.positionStrategy = config.positionStrategy || this.overlay.position().global().centerHorizontally().top();
    // I'm not sure what special behavior some modals may have, so turning off the autofocus for now
    config.autoFocus = undefined;
  }

  private makeCompatibleWithBootstrapModalAfterOpen(dialogRef: ApDialogRef) {
    // https://github.com/valor-software/ngx-bootstrap/blob/v9.0.0/src/component-loader/component-loader.class.ts#L410-L413
    // We should be using 'data' instead of 'initialState' going forward.
    // We have to do this after the ctor, so it doesn't get overwritten by default values in subclass ctor.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    Object.assign(dialogRef.componentInstance!, dialogRef.config.initialState);
    // If there are issues with assigning it here, then there are some options for where to do this:
    // alternative 1: assign initialState after dialog.open() call in ApModalService with the returned
    // DialogRef.componentInstance (this current code)
    // alternative 2: assign initialState in subclass override CdkDialogContainer.attachComponentPortal()
    // alternative 3: assign initialState in ApModalHelper.makeCompatibleWithBootstrapModalOnInit()
    // (some components are expecting these values to already be filled in before ngOnInit)
    // https://v15.material.angular.io/cdk/dialog/overview#providing-a-custom-dialog-container


    // if no dialogs were previously open (this is the first one opened), add this class to the document
    if (this.dialog.openDialogs.length === 1) {
      this.documentElement.classList.add('ap-cdk-bootstrap-modal-override');
      this.overlayContainer.getContainerElement().classList.add('ap-cdk-bootstrap-modal-override');
    }
  }

  showComponentModal$<TComponent extends AnyModalComponent<any>, TComponentState extends ExactlyPartial<TComponent, TComponentState>>(
    component: Type<TComponent>,
    config?: ApDialogConfig<InferModalData<TComponent>, TComponentState>,
  ): Observable<InferModalResult<TComponent>> {
    return from(this.showComponentModal(component, config));
  }

  // use this to more easily fill in providers property in ModalOptions with stateful services
  // todo: should I just use the Injector from the component calling this as the injector on the DialogConfig,
  //  and then I shouldn't have to manually provide these non-singleton classes.
  //  The only downside would be if there were ones that I explicitly didn't want to be available for the modals
  provideClasses(
    elementInjector: Injector,
    ...tokens: ApModalTokens
  ): StaticProvider[] {
    return tokens.map((token) => {
      return {
        provide: token,
        useFactory: () => {
          return elementInjector.get(token);
        },
      };
    });
  }
}
